// The list date filter's preset vocabulary, and each preset resolved to a range
// of the auction house's calendar days (ADR 024 — Time Model § 3c: "today" is the
// house's day, never the browser's). Pure: the caller supplies "today" — the
// server reads it with `todayLogicalDate(<house zone>)` — so this never reads a clock.

import { addLogicalDays, parseLogicalDate } from '@auctogon/time';

/** Every date preset a list's date filter offers, as a runtime list (the URL allowlist). */
export const LIST_DATE_PRESETS = [
  'today',
  'this_week',
  'this_month',
  'this_year',
  'last_30_days',
  'all_time',
  'custom',
] as const;

/** One date preset. */
export type ListDatePreset = (typeof LIST_DATE_PRESETS)[number];

/**
 * Every preset {@link presetToLogicalDateRange} resolves: the list presets plus
 * `last_month`, which the Auction summary's close-date range offers (BL-2901). It
 * stays out of `LIST_DATE_PRESETS`, so no list's date filter grows an option.
 */
export type ResolvableDatePreset = ListDatePreset | 'last_month';

/**
 * The Created presets a list's counts read counts (BL-2050 AC 4; shared with
 * Settlements by BL-2516): every pickable preset. `all_time` is "no pick" and
 * `custom` has no fixed range to count.
 */
export const LIST_CREATED_COUNT_PRESETS = ['today', 'this_week', 'this_month', 'last_30_days', 'this_year'] as const;

/** One counted Created preset. */
export type ListCreatedCountPreset = (typeof LIST_CREATED_COUNT_PRESETS)[number];

/** An inclusive range of logical dates (`YYYY-MM-DD`); null is an open end. */
export interface LogicalDateRange {
  from: string | null;
  to: string | null;
}

const LOGICAL_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** `YYYY-MM-DD` → its parts, or null when malformed. */
function splitLogicalDate(date: string): { year: number; month: number; day: number } | null {
  const match = LOGICAL_DATE_RE.exec(date);
  if (!match) return null;
  return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
}

/** Zero-padded `YYYY-MM-DD`. */
function joinLogicalDate(year: number, month: number, day: number): string {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** The last day of a month (1-based month). */
function lastDayOfMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/**
 * The years a range edge may name. Years 0–99 are out because `Date.UTC` reads them
 * as 1900–1999; 9999 is out because a To date's next day (the exclusive end the
 * reads compute) would need a five-digit year (BL-2541).
 */
const RANGE_YEAR_MIN = 1000;
const RANGE_YEAR_MAX = 9998;

/** One edge of a pasted range: a day that exists, in a year the reads can bound. */
function isUsableRangeEdge(edge: string): boolean {
  const day = parseLogicalDate(edge);
  return day != null && day.year >= RANGE_YEAR_MIN && day.year <= RANGE_YEAR_MAX;
}

/**
 * Parse a custom range's `from|to` URL value (each `YYYY-MM-DD` or empty), the
 * shape `encodeListDateRange` writes. A malformed edge is an open end, so a
 * pasted link with garbage filters nothing rather than failing the list. So is a
 * well-shaped day that doesn't exist (Feb 30) or a year out of range: either one
 * reached the SQL and failed the whole read (BL-2541). A start after the end (only
 * a pasted or hand-edited link can say so) is put in order, so the list never
 * empties behind a range no picker can make (BUG-2690). The strict reading below
 * refuses it instead.
 *
 * @param raw - The `<key>_range` value, or null when absent.
 * @returns The parsed inclusive range.
 */
export function parseLogicalDateRange(raw: string | null): LogicalDateRange {
  if (!raw) return { from: null, to: null };
  const [rawFrom, rawTo] = raw.split('|');
  const from = rawFrom && isUsableRangeEdge(rawFrom) ? rawFrom : null;
  const to = rawTo && isUsableRangeEdge(rawTo) ? rawTo : null;
  // `YYYY-MM-DD` with a four-digit year sorts as the calendar does (ADR 024 — Time Model).
  if (from != null && to != null && from > to) return { from: to, to: from };
  return { from, to };
}

/** A custom range read strictly: the range, or why it cannot be used. */
export type CustomLogicalDateRangeResult =
  | { ok: true; range: LogicalDateRange }
  | { ok: false; reason: 'missing' | 'invalid' };

/**
 * Parse a custom range's `from|to` value strictly, for an artifact that must not
 * quietly become a different report (the tax workbook, BUG-2349). A list reads a
 * bad edge as open on purpose (BL-2541, `parseLogicalDateRange`); a filing cannot.
 * An empty edge is a real open end (a one-sided range), so a range needs at least
 * one edge. Every named edge must be a day that exists in a usable year, there are
 * no more than two, and a start may not follow its end.
 *
 * @param raw - The `<key>_range` value, or null when absent.
 * @returns The range, or `missing` (no edge named) or `invalid` (anything unusable).
 */
export function parseCustomLogicalDateRange(raw: string | null): CustomLogicalDateRangeResult {
  const edges = (raw ?? '').split('|');
  if (edges.length > 2) return { ok: false, reason: 'invalid' };
  const [from = '', to = ''] = edges;
  if (from === '' && to === '') return { ok: false, reason: 'missing' };
  if ((from !== '' && !isUsableRangeEdge(from)) || (to !== '' && !isUsableRangeEdge(to))) return { ok: false, reason: 'invalid' };
  // `YYYY-MM-DD` with a four-digit year sorts as the calendar does.
  if (from !== '' && to !== '' && from > to) return { ok: false, reason: 'invalid' };
  return { ok: true, range: parseLogicalDateRange(raw) };
}

/**
 * Resolve a date preset to the house calendar days it covers, with the same
 * meanings the list date filter has always used: a week runs Sunday–Saturday,
 * "Last 30 days" is the 30 days before today through today, "Last month" is the
 * whole calendar month before today's (BL-2901), and `all_time` (or
 * `custom` with no range) is unbounded.
 *
 * @param input.preset - The preset; `custom` reads `customRange`.
 * @param input.today - The house's today, `YYYY-MM-DD` (`todayLogicalDate(zone)`).
 * @param input.customRange - The custom range, used only for `custom`.
 * @returns The inclusive logical-date range; both ends null when unbounded.
 */
export function presetToLogicalDateRange(input: {
  preset: ResolvableDatePreset;
  today: string;
  customRange?: LogicalDateRange;
}): LogicalDateRange {
  const { preset, today } = input;
  const parts = splitLogicalDate(today);
  if (!parts) return { from: null, to: null };
  const { year, month, day } = parts;
  switch (preset) {
    case 'today':
      return { from: today, to: today };
    case 'this_week': {
      const dayOfWeek = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
      const start = addLogicalDays(today, -dayOfWeek);
      return { from: start, to: addLogicalDays(start, 6) };
    }
    case 'this_month':
      return { from: joinLogicalDate(year, month, 1), to: joinLogicalDate(year, month, lastDayOfMonth(year, month)) };
    case 'last_month': {
      const lastYear = month === 1 ? year - 1 : year;
      const lastMonth = month === 1 ? 12 : month - 1;
      return { from: joinLogicalDate(lastYear, lastMonth, 1), to: joinLogicalDate(lastYear, lastMonth, lastDayOfMonth(lastYear, lastMonth)) };
    }
    case 'this_year':
      return { from: joinLogicalDate(year, 1, 1), to: joinLogicalDate(year, 12, 31) };
    case 'last_30_days':
      return { from: addLogicalDays(today, -30), to: today };
    case 'custom':
      return input.customRange ?? { from: null, to: null };
    case 'all_time':
    default:
      return { from: null, to: null };
  }
}
